/**
 * Counting days on a calendar, not hours on a clock (2026-10-04).
 *
 * "30 days to the race" is a fact about two calendar dates. Counted as elapsed
 * milliseconds ÷ 24 hours it is wrong whenever the clocks change in between: that
 * span is N × 24 h plus or minus one hour. `Math.ceil` over it reads N + 1 for the
 * first hour after midnight when the clocks went back, and N − 1 for the last hour
 * before midnight when they went forward; `Math.floor`, counting back from a past
 * day, makes the same two errors the other way round. Found at 00:06 US Eastern on
 * 2026-10-04: a countdown to 3 November read 31. CI never saw it — it runs in UTC,
 * which has no clock changes — and after 1 AM neither did a laptop.
 *
 * So a day is a NUMBER — how many days its calendar date is after 1 January 1970 —
 * and a count of days is one number minus another. A calendar date has no hours in
 * it, so there is nothing for a clock change to move.
 *
 * Use this wherever the answer is a number of DAYS between two dates (or weeks:
 * divide the days). Elapsed time is still right for what really is a length of time:
 * a cache's age, "paired 3 h ago", a bar that fills over a phase.
 *
 * Pure, no React. utils/__tests__/calendarDays.test.ts runs it inside zones that
 * change their clocks (scripts/in-time-zone.cjs), every day of a year.
 */

const DAY_MS = 86_400_000;

/** An instant's calendar date, read somewhere, as a day number. */
export type DayNumberOf = (d: Date) => number;

/**
 * The calendar date the PHONE shows for an instant, as a day number. `Date.UTC` is
 * only the arithmetic here: it turns a year, a month and a day into a count of days
 * on a calendar that never changes its clocks, so each day is exactly one more than
 * the day before (a 23 h or a 25 h day included).
 */
export const localDayNumber: DayNumberOf = (d) =>
  Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);

/**
 * Whole calendar days from the day `from` falls on to the day `to` falls on, both
 * read on the phone's calendar: 0 for two times on the same day, 1 when `to` is the
 * next day (one minute past midnight counts), negative when `to` is the earlier day.
 * NaN when either is not a date: check what you parsed before you count from it.
 */
export function calendarDaysBetween(from: Date, to: Date): number {
  return localDayNumber(to) - localDayNumber(from);
}
